/** The painted backdrop's palette: ground grade and tones, haze, the sky
 * bands, the far ranges. Split out of `arena.ts` on 2026-09-17; the painters
 * that consume these (`paintSky`, `paintFarScenery`, `paintGroundGrade`) are
 * `Arena` methods and stay there, next to the layer model in that file's
 * header.
 */

import { mixColor } from "../theme.js";
import { FIELD_TOP, TOPBAR_H } from "../grid.js";
import { battlefieldKey } from "../keys.js";

/**
 * How far the painted backdrop is dropped so its own horizon clears the
 * initiative bar. It is the bar's height: the band the painting puts at the very
 * top is exactly what the bar was covering.
 */
export const HORIZON_DROP = TOPBAR_H;

/**
 * The ground grade: one multiply over the whole painted field, laid down after
 * every ground pass and before the creatures are drawn on it.
 *
 * The defect it answers is a *ratio*, not a colour. In the classic a creature sits at
 * about the brightness of the ground it stands on; here it read as a dark
 * cut-out on a bright field. Measured over opaque pixels, Rec.709 luma:
 *
 *   The classic creature `Standing` frame 0, 77 sprites   81.4   per-sprite mean
 *   The classic `Combat_Dirt-Mountains` plate             101.5  whole, 91.0 over the
 *                                                   fraction of the frame our
 *                                                   board occupies
 *   The classic `Siege_combat.png`, a real composited     106.3  central ground box
 *   frame with the grid and creatures on it
 *
 * Against that, the shipped 1080p open-field shot ran its unshaded board at
 * **123.9** with its creatures at 60.8 - a ratio of 0.49 where the classic's own
 * frames give 0.74 (`Siege_combat`) to 0.90 (the dirt plate over the board's
 * fraction of the frame). The creature figure is the composited one: the actor
 * container sits above every darkening pass in this file, and a creature pixel
 * reaches the screen byte-identical to its atlas frame - exact-RGB membership in
 * the fourteen on-field units matched 0.00% of three empty-ground control boxes.
 *
 * The grade lives here rather than in the plates because the plates barely
 * reach the screen. Regressing composited ground against the plate pixel it was
 * painted from, over the 246k unshaded ground pixels of that shot, gives
 * `composited = 0.327 * plate + 87.2`: two thirds of what the player sees on the
 * board is this file's own paint. Re-grading the twenty-one backdrops in the
 * pack pipeline would therefore have moved the screen by a third of whatever it
 * did to the art, and baked it into the shipped plates. One multiply at the end
 * moves all of it and is a constant edit away from being undone.
 *
 * What it costs: the lattice is opaque `GRID_LINE_COLOR` (`ui.ts`; the
 * `COLOR.gridLine` this used to name was a dead duplicate and is gone), so its
 * contrast rises
 * with the ground it is drawn over. Over the open board it went from +64.5 to
 * +82.6, against the +70.5 the expansion gets over its own - a hair louder than the
 * reference rather than a hair fainter, which is the side of that floor the
 * lattice used to be on.
 *
 * 0.85 puts the unshaded board at 105.8 - `Siege_combat`'s 106.3 - and the
 * reach-shaded board at 58.1, against the 57.0 `battle-grid.spec.ts` records for
 * The expansion's own shaded ground (recorded, not re-checked: that reference file is no
 * longer in the tree). It is deliberately not enough to close the ratio on its
 * own. The other half of the gap is that our creature art is darker than
 * The classic's - 72.4 against 79.3 over the fourteen units on this field, 68.5
 * against 81.4 over all seventy-seven we have a `Standing` reference for - and
 * that cannot be answered from this end. The roster carries 1,996,501 opaque
 * pixels (8.79%) sitting at max channel exactly 9, one level above `BLACK_KEY`,
 * so anything that moves a creature pixel down feeds it back to the keyer.
 */
export const GROUND_GRADE = 0.85;

/**
 * `GROUND_GRADE` as a Pixi tint, for the things that are drawn ABOVE the
 * grade's own rect and so cannot be covered by it.
 *
 * `paintGroundGrade` is one black rect over the whole field, and it lives in
 * `background`, under the lattice. Everything that moved into `groundProps` to
 * get out from under the lattice - the moat, the drawbridge, the ground dress -
 * left the rect behind, and a multiply by 0.85 is a multiply by 0.85 whether it
 * arrives as a black overlay or as a tint on the thing itself. This keeps the
 * two routes at the same exposure, which is what stops the litter on a board
 * being brighter than the board.
 */
export const gradeTint = (c: number): number => mixColor(c, 0x000000, 1 - GROUND_GRADE);


/**
 * The three tones an arena's ground is marked in, read off that arena's own
 * plate.
 *
 * `paintGroundDetail` used to paint one sandy-tan palette - `0xe6d0a0`,
 * `0xdcc9a0`, `0xefd9a8`, `0xf0dcb0` over `0x46301a` - on all nineteen arenas,
 * with no terrain input at all, while `obstacleKey` in `keys.ts` has been
 * terrain-keyed the whole time. Measured on the 1080p shots over the *open*
 * (unshaded) cells of the board that carry no creature, obstacle or lattice
 * pixel, red minus blue of the composited ground against red minus blue of the
 * plate it was painted from:
 *
 *            composited   plate
 *   lava       +11.1      -8.1
 *   snow       +14.9      -1.0
 *   dirt       +54.5     +59.8
 *   cavern     +62.7     +73.4
 *
 * Two plates whose own ground is *cool* reached the screen warm: on lava the
 * light patches read as pale dust blown over volcanic rock, on snow the dark
 * ones as a dun stain. Luma was never the tell - the same measurement puts the
 * four boards at 52.3, 194.9, 101.0 and 72.3, which is four clearly different
 * surfaces - so a defect that lives entirely in hue survived two scenes that
 * both sit near plate luma 110.
 *
 * Each tone is the mean colour of a luminance band of the plate over exactly
 * the rectangle `paintGroundDetail` covers - x 115..1165, y 156..648 in arena
 * pixels, which is `FIELD_LEFT - 60` by `FIELD_TOP - 6`, `FIELD_W + 120` wide.
 * `shade` is that rectangle's darkest tenth, `mid` its middle tenth, `light`
 * its brightest tenth. Marks darker than `shade` and glints brighter than
 * `light` are derived from these two by multiply and screen rather than tabled,
 * so no mark can leave its terrain's hue.
 *
 * It is a measurement of the art, not a reading of it: the plates are packed
 * losslessly - each `battlefield.*` frame in `battle-0.webp` is byte-identical
 * to its `BATTLE/assets/battlefields/bf_*.png`, checked over all five
 * photographed arenas - so `battle-ground-tones.spec.ts` recomputes this table
 * straight from the library art and fails if it has drifted. An arena added to
 * the pack gets its entry by running that spec.
 *
 * Re-measured 2026-09-02 when the twenty-one plates were repainted as matte
 * paintings (`BATTLE/tools/build_battle.py`, `build/raw/painted`): every entry
 * here is that generator run over the painted plates. No floor is applied to
 * a tight spread: a floor is a hand-written number in the middle of a
 * measurement.
 */
interface GroundTones {
  /** Darkest tenth of the plate's ground: damp patches, lane beds. */
  readonly shade: number;
  /** Middle tenth: the body of a pebble. */
  readonly mid: number;
  /** Brightest tenth: sun-bleached patches, scour crests, lane crowns. */
  readonly light: number;
  /**
   * Mean Rec.709 luma of the plate over the board rectangle, one decimal. The
   * open board photographs as a mean, and on a dithered plate the mid-band
   * tone is not one: cavern's 45-55th percentile band sits 3 luma under its
   * mean because the plate's bright specks skew it, and `groundLevel` read
   * off `mid` missed the photographed board by 6.9 where `level` misses by 3.9.
   */
  readonly level: number;
}

const GROUND_TONES: Readonly<Record<string, GroundTones>> = {
  cavern: { shade: 0x2d1b1a, mid: 0x463431, light: 0x645851, level: 58.9 },
  lucky_meadow: { shade: 0x2c410e, mid: 0x56832f, light: 0x92a864, level: 105.1 },
  hexed_soil: { shade: 0x47443d, mid: 0x64625f, light: 0x7b7772, level: 96.9 },
  dirt: { shade: 0x6b4d39, mid: 0x8a6445, light: 0xa37f61, level: 109.0 },
  gloom_mist: { shade: 0x18120d, mid: 0x262620, light: 0x58604d, level: 41.7 },
  valor_fields: { shade: 0x7f5831, mid: 0xa98048, light: 0xc89f57, level: 129.5 },
  ember_fields: { shade: 0x201217, mid: 0x501d0e, light: 0x5e4033, level: 39.0 },
  grass: { shade: 0x695e24, mid: 0x6d841d, light: 0x889b2e, level: 114.3 },
  highlands: { shade: 0x2d2c1a, mid: 0x5f5f41, light: 0x73795c, level: 87.2 },
  blessed_earth: { shade: 0xb19a65, mid: 0xd1be8d, light: 0xf6e1a9, level: 192.6 },
  lava: { shade: 0x2c1d1b, mid: 0x263039, light: 0x43343c, level: 43.6 },
  mirror_pools: { shade: 0x989b96, mid: 0xb6bfc3, light: 0xd4eaeb, level: 191.9 },
  spell_clouds: { shade: 0x9c9dcd, mid: 0xbec7e3, light: 0xe1e9f8, level: 197.9 },
  ley_plains: { shade: 0x8b91b6, mid: 0xa8bed6, light: 0xc7d5f5, level: 182.3 },
  stone_barrens: { shade: 0x575b5d, mid: 0x83878e, light: 0x9faba8, level: 133.2 },
  rough: { shade: 0x5e452d, mid: 0x92714f, light: 0xd69e72, level: 123.3 },
  sand: { shade: 0xa98b6e, mid: 0xcdaf86, light: 0xe6c099, level: 175.7 },
  snow: { shade: 0xacb9c3, mid: 0xc9cdda, light: 0xd5e3ea, level: 210.1 },
  swamp: { shade: 0x373815, mid: 0x505c32, light: 0x7d9850, level: 91.6 },
  wasteland: { shade: 0x3c3129, mid: 0x5c534d, light: 0x6c665a, level: 85.6 },
  water: { shade: 0x2b464f, mid: 0x48677c, light: 0x7695a0, level: 99.2 },
};

/**
 * The tones for a terrain name, resolved through `battlefieldKey` so this table
 * and the plate can never disagree about which arena is being drawn - a name
 * `battlefieldKey` does not know falls back to grass, and so does this.
 */
export function groundTones(terrain: string): GroundTones {
  const id = battlefieldKey(terrain).slice("battlefield.".length);
  return GROUND_TONES[id] ?? GROUND_TONES["grass"]!;
}

/**
 * The colour the distance is, on each plate: the mean of that plate's own top
 * sixteen rows.
 *
 * Sixteen rather than "the sky", because there is no row number that is sky on
 * all twenty-one - the horizon runs from plate row ~24 on wasteland to ~60 on
 * snow - but every one of them is painted with its own atmosphere at the very
 * top of the frame, and that is the colour a haze pulls toward. Read straight
 * off `BATTLE/assets/battlefields/bf_*.png`, which the packer copies
 * losslessly, by the same method and from the same files as `GROUND_TONES`:
 * the top 24 rows of the 1920x1080 plate, which are the top 16 of the arena.
 *
 * The alternative is what shipped: one fixed `0xbfc6c0` on every arena, which
 * is a pale grey wash over the cavern's red ceiling and over lava's black
 * ridge. Haze is the colour of the air between, and the art already says what
 * that is.
 */
const HAZE_TONES: Readonly<Record<string, number>> = {
  cavern: 0x121111, lucky_meadow: 0xbcccd5, hexed_soil: 0xafaeab, dirt: 0x6f7e8f,
  gloom_mist: 0x5a5465, valor_fields: 0xefe6bc, ember_fields: 0x705c54, grass: 0x9daeb2,
  highlands: 0xb0c0cf, blessed_earth: 0xe3c88d, lava: 0x6c443d, mirror_pools: 0xcae7e7,
  spell_clouds: 0x383476, ley_plains: 0xa2a3e1, stone_barrens: 0xb3c8d3, rough: 0xb3bfc6,
  sand: 0xd8c3a6, snow: 0xbfcbd7, swamp: 0x819271, wasteland: 0x8d8572, water: 0xbbc8c9,
};

export function hazeTone(terrain: string): number {
  const id = battlefieldKey(terrain).slice("battlefield.".length);
  return HAZE_TONES[id] ?? HAZE_TONES["grass"]!;
}

/**
 * The arenas whose plate is a painting with its own horizon - sky, far ranges,
 * treeline and the open ground running continuously up into it - so the
 * synthetic sky, the hill ranges of `paintFarScenery`, the aerial haze of
 * `paintHaze` and the mottling of `paintGroundDetail` are not drawn over it.
 *
 * All twenty-one as of 2026-09-02 (`BATTLE/build/raw/painted`). The pixel-art
 * plates those replaced carried a flat band of identical peaks and a noise
 * field, and everything the synthetic passes did was an attempt to paint a
 * landscape over that; the classic's arenas are paintings, and now so are ours. The
 * synthetic passes stay for a plate that is not in this set - the fallback
 * for a new terrain packed before it is painted.
 */
const PAINTED_HORIZON: ReadonlySet<string> = new Set([
  "cavern", "lucky_meadow", "hexed_soil", "dirt", "gloom_mist", "valor_fields",
  "ember_fields", "grass", "highlands", "blessed_earth", "lava", "mirror_pools",
  "spell_clouds", "ley_plains", "stone_barrens", "rough", "sand", "snow", "swamp",
  "wasteland", "water",
]);

/** Whether `terrain`'s plate carries its own painted horizon; see `PAINTED_HORIZON`. */
export function paintedHorizon(terrain: string): boolean {
  return PAINTED_HORIZON.has(battlefieldKey(terrain).slice("battlefield.".length));
}

/**
 * Where the haze band starts, peaks and dies, in arena pixels.
 *
 * The peak is 44 because that is where the old pass put its top edge, and that
 * number was right - the twenty-one plates carry their sky/ground join between
 * rows 24 and 60, measured off the art, and 44 is the middle of that band. What
 * was wrong was that 44 was an EDGE. It is now the top of a ramp that starts at
 * 8, so there is no row anywhere in the frame where the haze arrives at once.
 *
 * The foot is 176 rather than the old 96 because the plates' horizons scatter
 * over 36 rows and the haze has to be gone by the back rank of the board
 * (`FIELD_TOP` is 153) without ending in a line on the way there.
 */
export const HAZE_TOP = 8;
export const HAZE_HORIZON = 44;
export const HAZE_FOOT = 176;
export const HAZE_MAX = 0.16;

/**
 * The sky, and why the arena has to paint one at all.
 *
 * The twenty-one painted plates have no sky. They are cropped so the horizon's
 * foot lands about a hundred rows down (`BATTLE/tools/build_battle.py`), and
 * above that foot is hill, mesa or treeline all the way to the plate's own top
 * row - the raw 1920x1280 paintings in `BATTLE/build/raw/painted` have no sky
 * band either, so there is none to recover by re-cropping. What the frame then
 * shows above the board is a flat low-contrast haze with no ground/sky boundary,
 * and that is a difference from the classic anyone can see without a ruler.
 *
 * Measured, 1920x1080, the whole arena interior:
 *
 *                                    p5    p50    p95
 *   ours, open field (2026-09-08)   33.7   88.6  122.6
 *   a reference screenshot   35.1   86.4  175.0
 *
 * The two boards are the same brightness - our rows 168..968 mean 83..92
 * against the reference's 78..90 - and the whole of that 52-point gap at p95 is
 * the reference's top band: its rows 8..88 mean 176.8 where ours mean 79.5.
 * There is no highlight anywhere in our frame because there is no sky in it.
 *
 * So a sky is painted OVER the plate's own top band. The plate is not moved:
 * an earlier cut dropped it by the sky's height and that pushed the painting's
 * hill feet down onto the back rank, which is the one thing the far band cannot
 * spend. What the sky covers instead is the top `SKY_H` rows of the plate,
 * which are its haziest and flattest - and the cut it makes across them is
 * hidden by `paintSky`'s own ridges, which stand where it lands.
 *
 * 88 rows, and the reference sets the band. It was 72, and 46 of those 72 are
 * behind the rail and the queue strip (`QUEUE_Y`), so what a player could see
 * of the sky was 26 arena rows - 39 device at 1080p - between the chrome and
 * the far ridge's crest. The classic spends 30-55 of 556 rows on sky with NO chrome
 * over any of it; 88 leaves 42 rows visible under ours, which is the same
 * band of open air the references show. On the classic's own 800x556 plates the sky
 * runs from row 0 to where the mean falls off the top plateau -
 * Combat_Grass-Trees 211.7 at row 0, 191 at 20, 124 at 40; Combat_Rough 205.9,
 * 201, 133; Combat_Wasteland 185.4, 199.6, 186.8 with the fall at 60 - so
 * 30-55 of 556 rows, 5.4-9.9%. Ours has to carry the queue as well: the strip
 * is docked on the rail and takes arena rows 13..43 (`QUEUE_Y` in `ui.ts`), so
 * of these 72 rows the player sees 43..72 plus the ridges, which is 44 device
 * rows of open sky at 1920x1080 and the brightest 44 of them - the ramp runs
 * *toward* the horizon (see `SKY_FOOT_LIFT`), so the band that survives the
 * strip is the lit one.
 *
 * What it costs is the top of the painted hill band: on bf_dirt the hills run to
 * about arena row 85, so 13 rows of them are left under the ridges. That is the
 * trade this file has to make and it is worth naming - the paintings are good
 * and this covers the top half of their scenery - but a hazy hill top with no
 * sky over it was reading as fog, and the ridges put a horizon back.
 */
export const SKY_H = 88;
/**
 * How far the far ridge's crest rides up into the sky, in arena pixels.
 *
 * The foot is not a ruled line. A sky that ends on one is the same defect a
 * critic measured on the bailey's rear wall - "a perfectly ruled horizontal
 * line ~400 device px long" - and no the classic plate has one: the sky ends on a
 * hill crest, a mesa line or a treeline. Two sines an octave and a bit apart,
 * so the crest never repeats across the 1280 and never reads as a wave.
 */
export const SKY_CREST = 9;
/**
 * How far the ridges' feet run below the sky's, in arena pixels.
 *
 * Two ranges, not one: a single silhouette against a gradient reads as a cut
 * mask however good its crest is, because everything behind it is air and
 * everything in front of it is the same tone. A far range hazed most of the way
 * to the sky with a near one in front of it, darker and lower, is aerial
 * perspective - the cue that says "land going away from you" - and it is what
 * every the classic arena's horizon has. The near range's foot reaches far enough to
 * cover the cut across the plate's own hills.
 *
 * It also sets how DEEP the near range is: its crest is at `SKY_H + 3` less
 * the crest amplitude and its foot here, so 16 gives it 22 rows of body. The
 * foot is where the slid plate meets the unslid one - a content seam - so it
 * has to land near the board's top edge (`FIELD_TOP` = 96.7), where the
 * ground is empty, rather than among the back rank's feet: 88 + 16 puts it on
 * 104, seven rows into the top of the back row's cells and 50 above where
 * anything stands.
 */
export const SKY_RIDGE_FOOT = 16;
/**
 * How far the plate is slid down inside the near range's mask, in arena pixels.
 *
 * The range shows the painting's own rows `SKY_H + 3 - SKY_RIDGE_SLIDE`
 * downward - on bf_dirt, rows 39..57, which is its dead-tree crest and the dry
 * scrub under it. Slid any further the range shows open plain and stops being a
 * range; any less and it repeats the rows the sky has just covered.
 */
export const SKY_RIDGE_SLIDE = 36;
/**
 * How far the near range is pulled into the air at its crest.
 *
 * 0.34, not 0.5. The near range is the PLATE's own painting - bf_dirt's dead
 * trees and dry scrub, bf_grass's conifers - and half of it mixed to the air
 * tone left a smear with no object in it: measured across the band on the
 * 2026-09-08 shot (device x300..600, rows 108..138) the std was 25 where the
 * source rows measure 23 at a mean 40 luma higher. At a third the trees are
 * still trees and the range still recedes.
 */
export const SKY_RIDGE_HAZE = 0.24;
/**
 * How many rows of the near range's foot are ramped into the ground's own
 * tone; see the toe in `paintSky`.
 */
export const SKY_TOE = 16;
/** How far the near range's foot wanders, in arena px; see the shape below. */
export const SKY_FOOT_BREAK = 7;
/**
 * **A sky gets brighter toward its horizon, not darker**, and the first cut of
 * this pass had it the other way round.
 *
 * Measured down the expansion's own wasteland battlefield
 * (`expansion-gallery_dread_battle3`, 720x450), row means:
 *
 *   y= 2  (149,194,224) L=186.9      y=28  (186,202,210) L=198.9
 *   y=10  (159,198,221) L=191.4      y=34  (191,199,202) L=197.5
 *   y=22  (183,205,218) L=201.1      y=40  (188,190,189) L=189.6
 *   then the mesas: y=52 L=161.7, y=64 L=132.4
 *
 * so the zenith is the *saturated* end and the air thickens to a warm white at
 * the horizon before the land starts. Laid the other way - a bright top ramping
 * down into `hazeTone` - the whole band reads as more of the same haze the
 * plate already has, which is what the first cut measured: p95 122.6 -> 142.0
 * where this one reaches 165+.
 *
 * The lift is toward a warm daylight white rather than 0xffffff because the expansion's
 * horizon row is (191,199,202) - still a colour.
 *
 * A dark arena is not lit that way and does not get it: lava's own plate goes
 * from (121,49,65) at the top row to (23,19,19) by row 40 (Combat_Lava), so a
 * zenith under `SKY_DAYLIGHT` takes its foot toward the plate's own haze
 * instead and thickens into the murk rather than out of it.
 */
export const SKY_HORIZON_WHITE = 0xf2ece2;
export const SKY_FOOT_LIFT = 0.45;
/** Above this zenith luma the sky is a daylight one; see `SKY_FOOT_LIFT`. */
export const SKY_DAYLIGHT = 120;

/**
 * The colour at the top of the sky, per terrain, read off the classic's own plates.
 *
 * Each is the median of the top four rows of the named `refs/battle/Combat_*`
 * plate (a median rather than a mean because on the tree-line plates the top
 * rows are part scenery, and the median is then the sky). Measured 2026-09-08:
 *
 *   Combat_Grass-Trees      #c6d6de   Combat_Rough           #b5cef7
 *   Combat_Wasteland        #94c4e1   Combat_Highlands       #a2caf2
 *   Combat_Snow-Mountains   #94a5c7   Combat_Sand-Desert     #73a5ff
 *   Combat_Rockland         #8cd8e7   Combat_Cursed-Ground   #7c94a6
 *   Combat_Magic-Clouds     #739cef   Combat_Magic-Plains    #829eb9
 *   Combat_Clover-Field     #7b94de   Combat_Fields-of-Glory #f4b867
 *   Combat_Holy-Ground      #3b775e   Combat_Swamp           #293121
 *   Combat_Lava             #662b4a   Combat_Subterranean    #5f3118
 *   Combat_Fiery-Fields     #211818   Combat_Evil-Fog        #102131
 *   Combat_Lucid-Pools      #ffffff   Combat_Boat            #312110
 *
 * Three are not a straight copy and all three are named here rather than left
 * to be rediscovered. `dirt` takes Combat_Wasteland's #94c4e1,
 * because our bf_dirt is an open dead-scrub plain and no single the classic *dirt* plate
 * is one: Dirt-Trees' top row is canopy (#81a898) and Dirt-Mountains' is an
 * unusually clear blue (#adf3ff), and their mean is a grey-teal that reads as
 * more haze over a hazy plate rather than as sky. `mirror_pools` is held two
 * levels off #ffffff, because a sky at 255 is the only surface in the frame
 * that cannot be lit further and it reads as a hole rather than as light.
 * `water`'s the classic plate is a boat deck, so it takes Combat_Sand-Beach's sea sky.
 */
const SKY_ZENITH: Readonly<Record<string, number>> = {
  cavern: 0x5f3118, lucky_meadow: 0x7b94de, hexed_soil: 0x7c94a6, dirt: 0x94c4e1,
  gloom_mist: 0x102131, valor_fields: 0xf4b867, ember_fields: 0x211818, grass: 0xc6d6de,
  highlands: 0xa2caf2, blessed_earth: 0x3b775e, lava: 0x662b4a, mirror_pools: 0xf2f4f6,
  spell_clouds: 0x739cef, ley_plains: 0x829eb9, stone_barrens: 0x8cd8e7, rough: 0xb5cef7,
  sand: 0x73a5ff, snow: 0x94a5c7, swamp: 0x293121, wasteland: 0x94c4e1, water: 0xdde6ec,
};

export function skyZenith(terrain: string): number {
  const id = battlefieldKey(terrain).slice("battlefield.".length);
  return SKY_ZENITH[id] ?? SKY_ZENITH["grass"]!;
}

/**
 * Cloud bands: how many, and how far the brightest of them lifts the sky.
 *
 * The classic's skies are not flat ramps - Combat_Rough and Combat_Wasteland both carry
 * banded cloud - and a 42-row ramp with nothing in it reads as a gradient
 * rather than as air. The bands are elongated lenses (18:1), laid out from the
 * terrain's own key so the same arena paints the same sky every time, and the
 * lift is toward the zenith's own tint pushed to white rather than toward white
 * itself, so a lava sky clouds red and not grey.
 */
export const CLOUD_BANDS = 5;
export const CLOUD_LIFT = 0.34;

/** Rec.709 luma of a packed 0xRRGGBB. */
export const lumaOf = (c: number): number =>
  0.2126 * ((c >> 16) & 255) + 0.7152 * ((c >> 8) & 255) + 0.0722 * (c & 255);

/**
 * How bright this arena's open board comes out, in screen luma, before anything
 * is marked on it.
 *
 * Two things need it and neither can take a screenshot: the reach shade has to
 * know how much ground there is to take (`ui.ts`), and an obstacle has to know
 * what light it is standing in (`obstacleTint`). Both used to assume the answer
 * was the same on every arena, and it is not - the open board runs from luma 29
 * on lava to 214 on snow.
 *
 * The estimator is the plate's own middle tenth, graded: `luma(mid) *
 * GROUND_GRADE`. Checked against the 1920x1080 shots by the method in
 * `battle-grid.spec.ts` - the 21 open cells that carry no creature, obstacle or
 * lattice pixel - it reads
 *
 *            estimate   measured
 *   lava        26.4      29.13
 *   cavern      53.6      56.00
 *   dirt        93.1      94.45
 *   snow       216.8     213.97
 *
 * so it is good to about 3 luma over a range of 185, which is well inside what
 * either caller does with it. It is deliberately the plate rather than the shot:
 * a number read off a screenshot goes stale the next time anything on the ground
 * moves, which is exactly how `GROUND_SHADED` and `GROUND_OPEN` went stale.
 */
export function groundLevel(terrain: string): number {
  return groundTones(terrain).level * GROUND_GRADE;
}

/**
 * The middle distance: two ranges of rolling hills between the plate's own
 * ridge and the board's back edge, in arena rows relative to `FIELD_TOP`.
 *
 * Every the classic arena is a painted landscape whose depth is in one strip -
 * `Combat_Grass-Trees` runs the ground up into three overlapping ranges of
 * hills with a treeline on each, and `Combat_Rough` into a line of crags - and
 * the eleven rows of hexes are laid on the flat in front of it. Our plates
 * carry a ridge at the top and then run straight into the board, and what
 * stood in between for a round was a picket line of twenty-four hazed cover
 * sprites at one depth: a critic measured it as "props lined up on a shelf,
 * not a horizon", and they were right - it was a shelf.
 *
 * Each range is the plate's **own ground**, taken from the middle of the
 * painting, cut to a rolling crest and pulled toward the arena's haze tone by
 * `haze`; the far range more so, the near one less, which is aerial
 * perspective and the one cue the eye cannot argue with. `foot` is where the
 * range meets the ground, `amp` how high its crests rise, `lambda` the
 * scale of the crest line in pixels per radian (a crest every ~290 and ~180
 * pixels; the first pass had 230 and 150 here, which is one wave across the
 * whole frame and read as a flat strip of water), `shade` how much darker than the open
 * board the hill face is, and `drift` how far the plate texture is slid
 * sideways so the two ranges are not the same rows of dirt twice.
 */
interface HillRange {
  readonly foot: number;
  readonly amp: number;
  readonly lambda: number;
  readonly phase: number;
  readonly haze: number;
  readonly shade: number;
  readonly drift: number;
}
export const HILL_RANGES: readonly HillRange[] = [
  // The far range: broad, high and mostly air. Its crest is the skyline, and
  // the sky is painted down to it (`SKY_TOP_LIFT`), so the plate's own pixel
  // mountain band - a flat strip of identical peaks a critic read as "a flat
  // band of pixel mountains" - is covered from the rail to the first hill.
  // The feet are well above the board: `Combat_Dirt-Dead-Trees` runs 20 rows
  // of open ground between its treeline and the grid's top edge (65..86 of
  // 556), and ours ran the near range's foot straight into the back row's
  // cell tops, so the hex-serrated top of the reach shading was also the
  // edge of the scenery.
  { foot: -54, amp: 30, lambda: 62, phase: 4.1, haze: 0.66, shade: 0.16, drift: 0.05 },
  { foot: -38, amp: 30, lambda: 40, phase: 0.9, haze: 0.34, shade: 0.1, drift: -0.37 },
  // The near range is LIT, not a dark band: `Combat_Grass-Mountains` paints
  // its nearest slopes a shade lighter than the field with the shade only
  // under the crests, and at 0.24 ours was a dark strip with a ruled foot.
  { foot: -24, amp: 18, lambda: 27, phase: 2.6, haze: 0.12, shade: 0.04, drift: 0.19 },
];
/**
 * Where the nearest range meets the open ground, in arena rows. Exported so
 * `battle-arena.spec.ts` can hold the band of open ground between the
 * scenery's foot and the back row's cell tops: the classic runs about 20 of 556 rows
 * of bare field there, and when ours was zero the reach shading's serrated top
 * edge doubled as the edge of the scenery.
 */
export const SCENERY_FOOT = FIELD_TOP + HILL_RANGES[HILL_RANGES.length - 1]!.foot;
/**
 * How far toward white the sky is at the top rail, against the arena's haze
 * tone at the skyline. The classic's skies (`Combat_Dirt-Trees`, `Combat_Rough`)
 * are palest at the top of the frame and thicken to the haze at the horizon;
 * the lift is small because the haze tone is already the plate's own top
 * rows, which on a cavern is a red ceiling and not a sky.
 */
export const SKY_TOP_LIFT = 0.28;
/**
 * The treeline on each crest: how tall a tree is, in arena pixels, at the
 * near range, and how dense the line is (one tree per this many pixels). The
 * far ranges take `TREELINE_FAR` of both. The classic's `Combat_Dirt-Trees` carries
 * its treeline as a *texture* - a serrated dark edge of a few hundred conifers
 * 8-30px tall on 800px - and that is what this draws: not props, a texture.
 */
export const TREELINE_H = 13;
export const TREELINE_STEP = 3;
export const TREELINE_FAR = 0.6;
/**
 * How much darker the face of a hill is at its foot than at its crest. The
 * first pass laid one flat haze over each range and the band read as a strip
 * of water: a hill has a lit skyline and a shaded valley, and the gradient is
 * the whole of what says "this stands up".
 */
export const HILL_VALLEY_SHADE = 0.2;
/**
 * How many props stand on the near range, and in how many copses. Six, not
 * twenty-four: the reference paints its treeline as a texture on the hill and
 * puts at most a handful of individual trees in the middle distance.
 */
export const FAR_COUNT = 6;
export const FAR_CLUSTERS = 2;
/** Share of the line that is a tree where the palette offers one. */
export const FAR_TREE_SHARE = 0.7;
/** Name fragments that make a blocking prop a tree (or a standing plant). */
export const FAR_TREEISH: readonly string[] = [
  "tree", "oak", "birch", "pine", "palm", "bush", "hazel", "rose", "hedge", "reeds",
  "mangrove", "cactus", "spire", "cone", "toadstool",
];
/**
 * Height of a far prop in row pitches. A blocker on the field is capped at
 * `MAX_UNIT_H` = 3.08 pitches and draws about 1.3-2.0; a tree on the hills
 * runs 0.45-0.7, so it is always smaller than one a stack has to walk around.
 */
// 0.42 and 0.3, down from 0.7 and 0.45: at 0.7 a dead tree on the ridge was
// 52 device px against a 13px treeline texture beside it, and read as a 2x
// sprite pasted on a painting. A far tree is a member of the treeline now,
// a little taller than its neighbours and no more.
export const FAR_H_NEAR = 0.42;
export const FAR_H_FAR = 0.3;
/** How far toward the arena's haze tone a far prop is pulled. */
export const FAR_HAZE_NEAR = 0.45;
export const FAR_HAZE_FAR = 0.62;
/**
 * Blocking sprites that are furniture rather than landscape, by name fragment:
 * none of these belongs on a skyline. The list is consulted with `includes`,
 * so `obs_dry_well` and `obs_rusted_boiler` are both caught by one entry each.
 */
export const FAR_EXCLUDE: readonly string[] = [
  "well", "cart", "fence", "watchfire", "woodpile", "column", "arch", "boiler",
  "vein", "pool", "vent", "ring", "bones", "hole", "stump", "log", "iceblock",
  "shard", "crack", "ruined",
];

/**
 * Where the grade starts and how long it takes to reach full strength, in arena
 * rows. Every plate paints its own horizon in its top band, and a sky the frame
 * darkens is a sky nobody painted - the same complaint the vignette above
 * already carries. Taking each plate's row profile and asking where it first
 * settles to within 8% of its own ground median, the band ends by row 93 on
 * nineteen of the twenty-one; the exceptions are `hexed_soil` (111) and
 * `cavern` (210), and neither has a sky to protect - the cavern is a cave and
 * cursed ground's top band is *darker* than its floor, not brighter. So the
 * grade begins below the last real horizon and ramps in over the far ground,
 * reaching full a few rows above `FIELD_TOP`.
 */
// 40 and 56, not 96 and 72: `FIELD_TOP` came up from 162 to 97 with the row
// pitch (see `ROW0_Y`), and a ramp that reached full at 168 would have graded
// the back two rows of the board differently from the front nine. The ramp now
// runs over the far ground between the painted horizon and the field's own top
// edge, and is full one row above it.
export const GRADE_TOP = 40;
export const GRADE_FADE = 56;
